/**
 * Environment configuration management
 * Supports multiple environments: local, uat, production
 */

export type Environment = 'local' | 'uat' | 'production';

export interface EnvironmentConfig {
  env: Environment;
  apiBaseUrl: string;
  authLoginEndpoint: string;
  authRegisterEndpoint: string;
  authResetPasswordEndpoint: string;
  shopsEndpoint: string;
  catalogsEndpoint: string;
  menusEndpoint: string;
  qrCodeEndpoint: string;
  customerShopMenuBaseUrl: string; // Customer-facing shop menu URL
  turnstileSiteKey: string;
  logLevel: 'debug' | 'info' | 'warn' | 'error';
  isDevelopment: boolean;
  isProduction: boolean;
}

/**
 * Get environment variable with fallback
 */
const getEnvVariable = (key: string, defaultValue: string = ''): string => {
  let value = defaultValue;

  if (typeof process !== 'undefined' && process.env) {
    value = process.env[key] || defaultValue;
  }

  // For React Native and browser environments
  if (!value || value === defaultValue) {
    if (typeof window !== 'undefined' && (window as any).__ENV__) {
      value = (window as any).__ENV__[key] || defaultValue;
    }
  }

  // Additional check for Vite-specific environment variables
  if (!value || value === defaultValue) {
    if (typeof import.meta !== 'undefined' && import.meta.env) {
      value = (import.meta as any).env[key] || defaultValue;
    }
  }

  // Trim whitespace and newlines
  value = value.trim();

  console.log(`🔍 [Environment] Loading ${key}:`, {
    fromProcess: typeof process !== 'undefined' ? process.env?.[key] : 'N/A',
    fromWindow: typeof window !== 'undefined' ? (window as any).__ENV__?.[key] : 'N/A',
    fromImportMeta: typeof import.meta !== 'undefined' ? (import.meta as any).env?.[key] : 'N/A',
    final: value,
    isDefault: value === defaultValue
  });

  return value;
};

/**
 * Load and validate environment configuration
 */
export const loadEnvironmentConfig = (): EnvironmentConfig => {
  let env = (getEnvVariable('VITE_APP_ENV', 'local') as Environment) || 'local';

  // Detect environment based on hostname
  // IMPORTANT: Check UAT BEFORE production to avoid matching on soanch.com
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;

    // UAT environment - MUST CHECK FIRST before production
    if (hostname.includes('uatbazly.soanch.com') ||
        hostname.includes('uat-my-shop') ||
        hostname.includes('uat.')) {
      env = 'uat';
      console.warn('🎯 [Environment] Detected UAT environment from hostname:', hostname);
    }
    // Production environment
    else if (hostname.includes('admin.bazly.in') ||
             hostname.includes('vercel.app')) {
      env = 'production';
      console.warn('🎯 [Environment] Detected PRODUCTION environment from hostname:', hostname);
    }
    // Local environment (localhost, 127.0.0.1, etc.)
    else {
      env = 'local';
      console.warn('🎯 [Environment] Detected LOCAL environment from hostname:', hostname);
    }
  }

  // Get API base URL based on environment
  let apiBaseUrl: string;
  if (env === 'uat') {
    apiBaseUrl = getEnvVariable('VITE_API_BASE_URL', 'https://uatapi.soanch.com');
  } else if (env === 'production') {
    apiBaseUrl = getEnvVariable('VITE_API_BASE_URL', 'https://api.soanch.com');
  } else {
    apiBaseUrl = getEnvVariable('VITE_API_BASE_URL', 'http://localhost:8080');
  }

  // 🔒 FORCE CORRECT API URL based on environment
  let correctedApiBaseUrl = apiBaseUrl;

  // Fix common typos
  correctedApiBaseUrl = correctedApiBaseUrl.replace('.comn', '.com');
  correctedApiBaseUrl = correctedApiBaseUrl.replace('.comm', '.com');
  correctedApiBaseUrl = correctedApiBaseUrl.replace('.con', '.com');

  // 🚨 FORCE CORRECT URL FOR EACH ENVIRONMENT
  // IMPORTANT: Check UAT BEFORE production to avoid matching on soanch.com
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;

    // UAT environment - MUST CHECK FIRST
    if (hostname.includes('uatbazly.soanch.com') ||
        hostname.includes('uat-my-shop') ||
        hostname.includes('uat.')) {
      console.warn('🚨 [Environment] FORCING UAT API URL');
      correctedApiBaseUrl = 'https://uatapi.soanch.com';
    }
    // Production environment
    else if (hostname.includes('admin.bazly.in')) {
      console.warn('🚨 [Environment] FORCING Production API URL');
      correctedApiBaseUrl = 'https://api.soanch.com';
    }
  }

  // 🚨 DEBUG: API Base URL
  console.log('🌐 [Environment] API Base URL Configuration:', {
    environment: env,
    hostname: typeof window !== 'undefined' ? window.location.hostname : 'server-side',
    isProduction: env === 'production',
    fromEnvVar: getEnvVariable('VITE_API_BASE_URL', 'NOT_SET'),
    fallback: env === 'production' ? 'https://api.soanch.com' : 'http://localhost:8080',
    beforeCorrection: apiBaseUrl,
    afterCorrection: correctedApiBaseUrl,
    hadTypo: apiBaseUrl !== correctedApiBaseUrl,
    wasForced: typeof window !== 'undefined' && correctedApiBaseUrl === 'https://api.soanch.com' && apiBaseUrl !== 'https://api.soanch.com'
  });
  const authLoginEndpoint = getEnvVariable('VITE_AUTH_LOGIN_ENDPOINT', '/api/auth/login');
  const authRegisterEndpoint = getEnvVariable('VITE_AUTH_REGISTER_ENDPOINT', '/api/auth/register');
  const authResetPasswordEndpoint = getEnvVariable('VITE_AUTH_RESET_PASSWORD_ENDPOINT', '/api/auth/reset-password');
  const shopsEndpoint = getEnvVariable('VITE_SHOPS_ENDPOINT', '/api/shops');
  const catalogsEndpoint = getEnvVariable('VITE_CATALOGS_ENDPOINT', '/api/catalogs');
  const menusEndpoint = getEnvVariable('VITE_MENUS_ENDPOINT', '/api/shops');
  const qrCodeEndpoint = getEnvVariable('VITE_QR_CODE_ENDPOINT', '/api/shops');

  // Customer-facing shop menu base URL - configurable for different environments
  const customerShopMenuBaseUrl = getEnvVariable(
    'VITE_CUSTOMER_SHOP_MENU_BASE_URL',
    'https://bazly.in/web-menu'
  );

  // Turnstile site key based on domain
  let turnstileSiteKey = '1x00000000000000000000AA'; // Default test key

  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;

    // IMPORTANT: Check UAT FIRST before production to avoid soanch.com match
    // UAT environment - use test key (same as localhost)
    if (hostname.includes('uatbazly.soanch.com') ||
        hostname.includes('uat-my-shop') ||
        hostname.includes('uat.')) {
      turnstileSiteKey = '1x00000000000000000000AA'; // Test key for UAT
      console.warn('🔑 [Environment] Using TEST Turnstile key for UAT');
    }
    // Production domains - use real Cloudflare key
    else if (hostname.includes('admin.bazly.in') ||
             hostname.includes('vercel.app')) {
      turnstileSiteKey = '0x4AAAAAACNn4Kz0KyPcwkYD';
      console.warn('🔑 [Environment] Using PRODUCTION Turnstile key');
    }
    // Development domain - use test key that always passes
    else if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
      turnstileSiteKey = '1x00000000000000000000AA';
      console.warn('🔑 [Environment] Using TEST Turnstile key for localhost');
    }
  } else {
    // Server-side or build time - try to get from env
    const envKey = getEnvVariable('VITE_TURNSTILE_SITE_KEY', '');
    if (envKey && envKey !== '') {
      turnstileSiteKey = envKey;
    }
  }

  console.log('🔑 [Environment] Turnstile configuration:', {
    hostname: typeof window !== 'undefined' ? window.location.hostname : 'server-side',
    siteKey: turnstileSiteKey,
    isTestKey: turnstileSiteKey.startsWith('1x00000000'),
    isProductionKey: turnstileSiteKey.startsWith('0x4'),
    environment: env
  });

  const logLevel = (getEnvVariable('VITE_LOG_LEVEL', env === 'production' ? 'error' : 'debug') as any) || 'info';

  const isDevelopment = env === 'local';
  const isProduction = env === 'production';

  // Debug logging for production deployment
  if (typeof console !== 'undefined') {
    console.log('🔧 [Environment] Raw environment variables:', {
      VITE_APP_ENV: getEnvVariable('VITE_APP_ENV', 'NOT_SET'),
      VITE_API_BASE_URL: getEnvVariable('VITE_API_BASE_URL', 'NOT_SET'),
      VITE_CUSTOMER_SHOP_MENU_BASE_URL: getEnvVariable('VITE_CUSTOMER_SHOP_MENU_BASE_URL', 'NOT_SET'),
      VITE_TURNSTILE_SITE_KEY: getEnvVariable('VITE_TURNSTILE_SITE_KEY', 'NOT_SET'),
      NODE_ENV: typeof process !== 'undefined' ? process.env.NODE_ENV : 'NOT_AVAILABLE'
    });
    console.log('🔧 [Environment] Configuration loaded:', {
      env,
      apiBaseUrl,
      authLoginEndpoint,
      customerShopMenuBaseUrl,
      turnstileSiteKey,
      isDevelopment,
      isProduction
    });

    // Force production mode if we're on production domain
    const isProductionDomain = typeof window !== 'undefined' &&
      (window.location.hostname.includes('vercel.app') ||
       window.location.hostname.includes('admin.bazly.in'));

    if (isProductionDomain && env !== 'production') {
      console.warn('🚨 [Environment] Detected production domain but env is not production!', {
        hostname: typeof window !== 'undefined' ? window.location.hostname : 'unknown',
        detectedEnv: env,
        shouldBeProduction: true
      });
    }
  }

  return {
    env,
    apiBaseUrl: correctedApiBaseUrl,
    authLoginEndpoint,
    authRegisterEndpoint,
    authResetPasswordEndpoint,
    shopsEndpoint,
    catalogsEndpoint,
    menusEndpoint,
    qrCodeEndpoint,
    customerShopMenuBaseUrl,
    turnstileSiteKey,
    logLevel,
    isDevelopment,
    isProduction,
  };
};

// Create singleton instance
let configInstance: EnvironmentConfig | null = null;

/**
 * Get or create environment configuration
 */
export const getConfig = (): EnvironmentConfig => {
  if (!configInstance) {
    configInstance = loadEnvironmentConfig();
  }
  return configInstance;
};

/**
 * Build customer-facing shop menu URL with shopId
 * @param shopId - The shop ID to append to the URL
 * @returns Complete URL for customer-facing shop menu
 */
export const getCustomerShopMenuUrl = (shopId: string): string => {
  const config = getConfig();
  return `${config.customerShopMenuBaseUrl}?shopid=${shopId}`;
};

/**
 * Reset configuration (useful for testing)
 */
export const resetConfig = (): void => {
  configInstance = null;
};

/**
 * Log utility based on environment
 */
export const createLogger = (module: string) => {
  const config = getConfig();
  const logLevelMap = { debug: 0, info: 1, warn: 2, error: 3 };
  const currentLevel = logLevelMap[config.logLevel];

  return {
    debug: (message: string, data?: any) => {
      if (currentLevel <= 0) console.log(`[${module}] ${message}`, data);
    },
    info: (message: string, data?: any) => {
      if (currentLevel <= 1) console.log(`[${module}] ${message}`, data);
    },
    warn: (message: string, data?: any) => {
      if (currentLevel <= 2) console.warn(`[${module}] ${message}`, data);
    },
    error: (message: string, data?: any) => {
      if (currentLevel <= 3) console.error(`[${module}] ${message}`, data);
    },
  };
};

